import { useState, useMemo } from 'react';
import {
  ClipboardCheck, Sparkles, AlertTriangle, CheckCircle2, XCircle,
  HelpCircle, Loader2, Award, Target, FileText, Shield, BookOpen,
  ChevronDown, ChevronUp, Rocket, Coins, Gem, Building2, ArrowRight, Info,
  Zap,
} from 'lucide-react';
import { scoreListingReadiness } from '../services/api';
import { InfoTip } from '../components/ui';

const CHAINS = [
  { id: 'ethereum',  label: 'Ethereum' },
  { id: 'base',      label: 'Base' },
  { id: 'arbitrum',  label: 'Arbitrum' },
  { id: 'optimism',  label: 'Optimism' },
  { id: 'polygon',   label: 'Polygon' },
  { id: 'bsc',       label: 'BSC' },
  { id: 'avalanche', label: 'Avalanche' },
  { id: 'blast',     label: 'Blast' },
  { id: 'linea',     label: 'Linea' },
  { id: 'scroll',    label: 'Scroll' },
];

const DEFAULT_ATTESTED = {
  hasAudit: false, auditFirm: '',
  hasGithub: false, githubUrl: '',
  hasWhitepaper: false,
  kycFounders: false,
  hasLegalOpinion: false,
  activeSocial: false,
};

// Scenario presets pre-tick the attestation checkboxes for a typical project
// of each type. They're starting points, not promises — users adjust from the
// pattern. Helps issuers see what "complete" looks like for their token type.
const PRESETS = [
  {
    id: 'defi',
    label: 'DeFi protocol',
    icon: Coins,
    accent: 'cyan',
    blurb: 'Lending, DEX, derivatives, yield. Heaviest open-source bar.',
    attested: { hasAudit: true, hasGithub: true, hasWhitepaper: true, kycFounders: true, hasLegalOpinion: true, activeSocial: true },
  },
  {
    id: 'utility',
    label: 'Utility token',
    icon: Zap,
    accent: 'cyan',
    blurb: 'Powers a platform, app, or service. Token enables access, governance, or rewards.',
    attested: { hasAudit: true, hasGithub: true, hasWhitepaper: true, kycFounders: false, hasLegalOpinion: false, activeSocial: true },
  },
  {
    id: 'gamefi',
    label: 'GameFi / NFT project',
    icon: Gem,
    accent: 'purple',
    blurb: 'Has game/NFT code on GitHub, whitepaper, active community.',
    attested: { hasAudit: true, hasGithub: true, hasWhitepaper: true, kycFounders: false, hasLegalOpinion: false, activeSocial: true },
  },
  {
    id: 'memecoin',
    label: 'Memecoin / community token',
    icon: Rocket,
    accent: 'amber',
    blurb: 'Community-driven, often skips audit/docs. Realistic baseline.',
    attested: { hasAudit: false, hasGithub: false, hasWhitepaper: false, kycFounders: false, hasLegalOpinion: false, activeSocial: true },
  },
  {
    id: 'rwa',
    label: 'Stablecoin / RWA',
    icon: Building2,
    accent: 'emerald',
    blurb: 'Regulated. Audit, KYC, and legal opinion mandatory.',
    attested: { hasAudit: true, hasGithub: true, hasWhitepaper: true, kycFounders: true, hasLegalOpinion: true, activeSocial: true },
  },
];

function statusIcon(status) {
  if (status === 'pass') return <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />;
  if (status === 'fail') return <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />;
  if (status === 'partial') return <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />;
  return <HelpCircle className="w-4 h-4 text-gray-500 flex-shrink-0" />;
}

function gradeColor(grade) {
  if (grade === 'A') return 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30';
  if (grade === 'B') return 'text-cyan-300 bg-cyan-500/15 border-cyan-500/30';
  if (grade === 'C') return 'text-amber-300 bg-amber-500/15 border-amber-500/30';
  if (grade === 'D') return 'text-orange-300 bg-orange-500/15 border-orange-500/30';
  return 'text-red-300 bg-red-500/15 border-red-500/30';
}

function sourceBadge(source) {
  const map = {
    'on-chain':      { label: 'ON-CHAIN', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
    // Generalize the market-data badge so users don't see our pricing provider
    'coingecko':     { label: 'MARKET DATA', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
    'self-attested': { label: 'SELF-ATTESTED', cls: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
  };
  const s = map[source] || { label: source.toUpperCase(), cls: 'bg-dark-700 text-gray-400 border-dark-600' };
  return <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${s.cls}`}>{s.label}</span>;
}

export default function ListingReadinessPage() {
  const [contract, setContract] = useState('');
  const [chain, setChain] = useState('ethereum');
  const [attested, setAttested] = useState(DEFAULT_ATTESTED);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(true);
  const [appliedPreset, setAppliedPreset] = useState(null);

  async function runScore() {
    setError(null);
    setLoading(true);
    try {
      const res = await scoreListingReadiness({ contract, chain, attested });
      setResult(res?.data || res);
    } catch (e) {
      setError(e?.message || 'Score failed');
    } finally {
      setLoading(false);
    }
  }

  function toggleAttested(field) {
    setAttested(prev => ({ ...prev, [field]: !prev[field] }));
    setAppliedPreset(null); // user-edited, no longer matches preset
  }
  function setAttestedField(field, value) {
    setAttested(prev => ({ ...prev, [field]: value }));
  }
  function applyPreset(preset) {
    setAppliedPreset(preset.id);
    setAttested(prev => ({
      ...DEFAULT_ATTESTED,
      ...preset.attested,
      auditFirm: prev.auditFirm,
      githubUrl: prev.githubUrl,
    }));
  }

  // Rank exchanges best-to-worst so the headline can lead with the most likely
  const rankedExchanges = useMemo(() => {
    if (!result?.exchanges) return [];
    return Object.values(result.exchanges).sort((a, b) => b.score - a.score);
  }, [result]);

  const bestExchange = rankedExchanges[0];
  const topGap = result?.topGaps?.[0];

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-emerald-500/20 p-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
            <ClipboardCheck className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">Listing Readiness Scorecard</h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-mono">Real criteria · No guesswork</span>
            </div>
            <p className="text-xs text-emerald-200/70 mt-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Elite · Issuer Tools · Grade your token against each major CEX's public listing policy
              <InfoTip side="bottom">Grades against 5 CEXes (Binance, Coinbase, OKX, Kraken, Gemini). Self-attested items are CLAIMED, not verified — be honest, exchanges verify in their own review. NOT a guarantee.</InfoTip>
            </p>
          </div>
        </div>
      </div>

      {/* What is this tool? */}
      <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 overflow-hidden">
        <button
          onClick={() => setAboutOpen(o => !o)}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-dark-700/30 transition"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-white">What is this tool — and how do I use it?</div>
              <div className="text-[11px] text-gray-500">Plain-English walkthrough · 30 seconds</div>
            </div>
          </div>
          {aboutOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {aboutOpen && (
          <div className="px-5 pb-5 border-t border-dark-700/40 pt-4 space-y-4">
            <div>
              <div className="text-xs font-semibold text-emerald-300 uppercase tracking-wider mb-1.5">The problem</div>
              <p className="text-sm text-gray-300 leading-relaxed">
                Getting listed on a tier-1 CEX (Binance, Coinbase, OKX, Kraken, Gemini) takes months of prep and there's no public scorecard. You don't know if your token is 60% ready or 90% ready — until the listing team responds (or doesn't).
              </p>
            </div>
            <div>
              <div className="text-xs font-semibold text-emerald-300 uppercase tracking-wider mb-1.5">What this tool does</div>
              <p className="text-sm text-gray-300 leading-relaxed">
                Pulls real on-chain facts (contract verified, deployment date), real market data (market cap, volume, existing CEX listings), combines them with your self-attested project artifacts (audit, GitHub, KYC, legal opinion), and scores you against the <span className="text-white">public listing policy</span> of each of the 5 major CEXes. Each criterion is cited from the exchange's own published policy.
              </p>
            </div>
            <div>
              <div className="text-xs font-semibold text-emerald-300 uppercase tracking-wider mb-1.5">How to use it in 3 steps</div>
              <ol className="text-sm text-gray-300 leading-relaxed space-y-1.5 list-decimal pl-5">
                <li>Paste your <span className="text-white">contract address</span> and pick the chain.</li>
                <li>Tick the <span className="text-white">attestation checkboxes</span> for what your project actually has. Pick a scenario preset to start.</li>
                <li>Click <span className="text-white">Score readiness</span>. You'll get per-CEX grades, the highest-impact gaps to close, and a recommended next step.</li>
              </ol>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-200/90 leading-relaxed">
                <span className="font-semibold">How to read this:</span> Self-attested items are claimed by you, not verified by us — CEXes will verify them during their own review. A high score is a strong starting point, not a guarantee. Real listing decisions also weigh subjective factors (team interview, exclusivity, fees).
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Inputs */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5 space-y-3">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <Target className="w-4 h-4 text-emerald-400" /> Your token
            </h2>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Contract address</label>
              <input
                type="text"
                value={contract}
                onChange={e => setContract(e.target.value.trim())}
                placeholder="0x..."
                className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-emerald-400/40 focus:outline-none"
              />
              <p className="text-[10px] text-gray-500 mt-1">The ERC-20 contract address of the token you're scoring.</p>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Chain</label>
              <div className="grid grid-cols-3 gap-1.5">
                {CHAINS.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setChain(c.id)}
                    className={`px-2 py-1.5 rounded-md text-[11px] transition ${
                      chain === c.id
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/40'
                        : 'bg-dark-700/60 text-gray-400 hover:text-white border border-dark-600'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Scenario presets */}
          <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5 space-y-3">
            <div>
              <h2 className="text-sm font-bold text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-cyan-400" /> Project type
              </h2>
              <p className="text-[10px] text-gray-500 mt-1">Pick the closest match — we'll pre-tick what a typical project of that type has. Adjust below.</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {PRESETS.map(p => {
                const Icon = p.icon;
                const isActive = appliedPreset === p.id;
                const accents = {
                  amber: 'border-amber-500/60 text-amber-300',
                  cyan: 'border-cyan-500/60 text-cyan-300',
                  emerald: 'border-emerald-500/60 text-emerald-300',
                  purple: 'border-purple-500/60 text-purple-300',
                };
                return (
                  <button
                    key={p.id}
                    onClick={() => applyPreset(p)}
                    className={`text-left p-2.5 rounded-lg border bg-dark-900/30 hover:bg-dark-700/40 transition ${isActive ? accents[p.accent] : 'border-dark-700/50'}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon className={`w-3.5 h-3.5 ${isActive ? '' : 'text-gray-400'}`} />
                      <span className={`text-[11px] font-semibold ${isActive ? '' : 'text-white'}`}>{p.label}</span>
                      {isActive && <CheckCircle2 className="w-3 h-3 ml-auto" />}
                    </div>
                    <p className="text-[10px] text-gray-500 leading-snug">{p.blurb}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Self-attestation */}
          <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5 space-y-3">
            <div>
              <h2 className="text-sm font-bold text-white flex items-center gap-2">
                <Shield className="w-4 h-4 text-violet-400" /> Self-attestation
              </h2>
              <p className="text-[10px] text-gray-500 mt-1">Tick what's true about your project. We can't verify these on-chain; CEXes will verify manually during review.</p>
            </div>

            <AttestCheck label="Audited by reputable firm (CertiK / OpenZeppelin / Trail of Bits / Hacken / PeckShield)"
              checked={attested.hasAudit} onToggle={() => toggleAttested('hasAudit')}>
              {attested.hasAudit && (
                <input
                  type="text"
                  value={attested.auditFirm}
                  onChange={e => setAttestedField('auditFirm', e.target.value)}
                  placeholder="Audit firm name"
                  className="w-full mt-2 bg-dark-700 border border-dark-600 rounded-md px-2 py-1 text-white text-[11px] focus:border-violet-400/40 focus:outline-none"
                />
              )}
            </AttestCheck>

            <AttestCheck label="Public GitHub with active development"
              checked={attested.hasGithub} onToggle={() => toggleAttested('hasGithub')}>
              {attested.hasGithub && (
                <input
                  type="text"
                  value={attested.githubUrl}
                  onChange={e => setAttestedField('githubUrl', e.target.value)}
                  placeholder="https://github.com/..."
                  className="w-full mt-2 bg-dark-700 border border-dark-600 rounded-md px-2 py-1 text-white text-[11px] font-mono focus:border-violet-400/40 focus:outline-none"
                />
              )}
            </AttestCheck>

            <AttestCheck label="Whitepaper + tokenomics docs published"
              checked={attested.hasWhitepaper} onToggle={() => toggleAttested('hasWhitepaper')} />

            <AttestCheck label="Founders KYC'd (passport-verified)"
              checked={attested.kycFounders} onToggle={() => toggleAttested('kycFounders')} />

            <AttestCheck label="Legal opinion on token classification"
              checked={attested.hasLegalOpinion} onToggle={() => toggleAttested('hasLegalOpinion')} />

            <AttestCheck label="Active community (Twitter/Discord/Telegram)"
              checked={attested.activeSocial} onToggle={() => toggleAttested('activeSocial')} />
          </div>

          <button
            onClick={runScore}
            disabled={!contract || !/^0x[a-fA-F0-9]{40}$/.test(contract) || loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-500 text-dark-900 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed font-bold text-sm transition"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
            {loading ? 'Scoring…' : 'Score readiness'}
          </button>

          {error && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-2.5 text-xs text-red-300">
              {error}
            </div>
          )}
        </div>

        {/* Results */}
        <div className="lg:col-span-2 space-y-4">
          {!result && (
            <div className="bg-dark-800/40 rounded-2xl border border-dark-700/40 border-dashed p-10 text-center">
              <Award className="w-10 h-10 text-emerald-400/40 mx-auto mb-3" />
              <p className="text-sm text-gray-300 font-medium">Ready when you are.</p>
              <p className="text-[12px] text-gray-500 mt-2 max-w-md mx-auto leading-relaxed">
                Paste your contract, tick what you have, and click <span className="text-white font-semibold">Score readiness</span>. You'll get a 5-CEX scorecard, the gaps that hurt your score most, and a recommended next step.
              </p>
            </div>
          )}

          {result && (
            <>
              {/* Token overview */}
              <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <h3 className="text-base font-bold text-white">
                      {result.marketData?.name || 'Token'} {result.marketData?.symbol ? `(${result.marketData.symbol})` : ''}
                    </h3>
                    <p className="text-[11px] text-gray-500 font-mono mt-0.5">{result.chain} · {result.contract.slice(0,10)}...{result.contract.slice(-8)}</p>
                  </div>
                  <div className="text-right">
                    {result.onChain.ageDays != null && (
                      <p className="text-xs text-gray-400"><span className="text-white font-bold">{result.onChain.ageDays}</span> days old</p>
                    )}
                    {result.marketData?.marketCapUsd && (
                      <p className="text-xs text-gray-400">MC <span className="text-white font-bold">${(result.marketData.marketCapUsd/1e6).toFixed(2)}M</span></p>
                    )}
                  </div>
                </div>
                {result.onChain.contractVerified === null && (
                  <div className="mt-3 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2 text-[11px] text-amber-200/90">
                    ⚠ Chain explorer data unavailable for this contract — on-chain checks scored as unknown.
                  </div>
                )}
                {!result.marketData?.available && (
                  <div className="mt-3 bg-blue-500/5 border border-blue-500/20 rounded-lg px-3 py-2 text-[11px] text-blue-200/90">
                    ℹ This token isn't indexed in the public market data feed yet. Market-data-dependent rows will show unknown.
                  </div>
                )}
              </div>

              {/* Plain-English summary callout */}
              {bestExchange && (
                <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border border-emerald-500/30 rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Sparkles className="w-4 h-4 text-emerald-300" />
                    </div>
                    <div className="space-y-2 flex-1">
                      <h3 className="text-sm font-bold text-white">What this means for your listing strategy</h3>
                      <p className="text-xs text-gray-300 leading-relaxed">
                        Your strongest fit is <span className="text-white font-semibold">{bestExchange.label}</span> with a <span className={`font-mono font-semibold ${gradeColor(bestExchange.grade).split(' ')[0]}`}>Grade {bestExchange.grade}</span> ({bestExchange.score}/100). {bestExchange.region}. <span className="text-gray-400">{bestExchange.notes}</span>
                      </p>
                      {topGap && (
                        <p className="text-xs text-gray-300 leading-relaxed">
                          The single highest-impact thing to fix is <span className="text-amber-300 font-semibold">"{topGap.label}"</span> — closing this gap would gain back <span className="text-emerald-300 font-mono font-semibold">{topGap.totalImpact.toFixed(0)} points</span> across {topGap.exchanges.length} of 5 exchanges.
                        </p>
                      )}
                      {rankedExchanges.length > 1 && (
                        <p className="text-xs text-gray-400 leading-relaxed">
                          Full ranking: {rankedExchanges.map(ex => `${ex.label} (${ex.score})`).join(' → ')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Exchange scorecards */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Award className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-sm font-bold text-white">Per-exchange scorecards</h3>
                  <span className="text-[10px] text-gray-500">— ranked best fit first</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {rankedExchanges.map((ex, idx) => (
                    <div key={ex.id} className={`bg-dark-800/60 rounded-xl border p-4 ${idx === 0 ? 'border-emerald-500/40 ring-1 ring-emerald-500/20' : 'border-dark-700/50'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-1.5">
                            {idx === 0 && <span className="text-emerald-400 text-[10px]">★</span>}
                            <p className="text-sm font-bold text-white">{ex.label}</p>
                          </div>
                          <p className="text-[10px] text-gray-500">{ex.region}</p>
                        </div>
                        <div className={`text-2xl font-bold font-mono px-2.5 py-0.5 rounded-lg border ${gradeColor(ex.grade)}`}>
                          {ex.grade}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2 mb-3">
                        <span className="text-3xl font-bold font-mono text-white">{ex.score}</span>
                        <span className="text-[11px] text-gray-500">/ 100</span>
                      </div>
                      <div className="h-1.5 bg-dark-700/60 rounded-full overflow-hidden mb-3">
                        <div className={`h-full rounded-full ${ex.score >= 70 ? 'bg-emerald-500' : ex.score >= 55 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${ex.score}%` }} />
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-gray-500 mb-2">
                        <span>Data completeness</span>
                        <span className={`font-mono ${ex.completenessPct >= 80 ? 'text-emerald-400' : ex.completenessPct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{ex.completenessPct}%</span>
                      </div>
                      <p className="text-[10px] text-gray-500 leading-relaxed">{ex.notes}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top gaps with next-step recommendation */}
              {result.topGaps?.length > 0 && (
                <div className="bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/30 rounded-2xl p-5">
                  <h3 className="text-sm font-bold text-amber-300 flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4" /> Top gaps to close — ranked by impact
                  </h3>
                  <p className="text-[11px] text-amber-200/70 mb-3">Sorted by total points lost across all 5 exchanges. Start at the top.</p>
                  <div className="space-y-2">
                    {result.topGaps.slice(0, 6).map((g, i) => (
                      <div key={i} className={`rounded-lg p-3 border ${i === 0 ? 'bg-amber-500/10 border-amber-500/40' : 'bg-dark-900/40 border-amber-500/10'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {i === 0 && <ArrowRight className="w-3.5 h-3.5 text-amber-400" />}
                              <span className="text-sm font-semibold text-white">{g.label}</span>
                              {sourceBadge(g.source)}
                            </div>
                            <p className="text-[10px] text-gray-500">Impacts: {g.exchanges.join(', ')}</p>
                          </div>
                          <span className="text-xs font-mono text-amber-300 whitespace-nowrap">−{g.totalImpact.toFixed(0)} pts total</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {result.topGaps[0] && (
                    <div className="mt-3 pt-3 border-t border-amber-500/20 text-[11px] text-amber-200/80 leading-relaxed">
                      <span className="font-semibold text-amber-300">Recommended next step:</span> Address <span className="text-white">"{result.topGaps[0].label}"</span> first — it's the single highest-leverage move across your scorecards.
                    </div>
                  )}
                </div>
              )}

              {/* Detailed per-CEX scoring */}
              <details className="bg-dark-800/60 rounded-2xl border border-dark-700/50 p-5">
                <summary className="text-sm font-bold text-white flex items-center gap-2 cursor-pointer">
                  <FileText className="w-4 h-4 text-emerald-400" /> Full per-exchange criteria (click to expand)
                </summary>
                <div className="mt-4 space-y-4">
                  {rankedExchanges.map(ex => (
                    <div key={ex.id} className="border-l-2 border-emerald-500/30 pl-4">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-sm font-bold text-white">{ex.label}</h4>
                        <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${gradeColor(ex.grade)}`}>{ex.grade} · {ex.score}</span>
                        <span className="text-[10px] text-gray-500">Data: {ex.completenessPct}%</span>
                      </div>
                      <div className="space-y-1.5">
                        {ex.rows.map((row, i) => (
                          <div key={i} className="flex items-start gap-2 text-[11px]">
                            {statusIcon(row.status)}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-white">{row.label}</span>
                                {sourceBadge(row.source)}
                                <span className="text-gray-500 font-mono">{row.pointsEarned}/{row.weight} pts</span>
                              </div>
                              <p className="text-gray-500 mt-0.5">{row.detail}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </details>

              {/* Disclaimer */}
              <div className="bg-dark-900/40 rounded-xl border border-dark-700/40 p-4 text-[11px] text-gray-500 leading-relaxed">
                {result.disclaimer}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AttestCheck({ label, checked, onToggle, children }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer hover:bg-dark-700/30 -mx-2 px-2 py-1.5 rounded">
      <input type="checkbox" checked={checked} onChange={onToggle} className="mt-0.5 accent-violet-500" />
      <div className="flex-1">
        <span className="text-[11px] text-gray-300 leading-snug">{label}</span>
        {children}
      </div>
    </label>
  );
}
